import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Users, Clock, Calendar, UserCheck, TrendingUp, Bell, 
  ArrowRight, Briefcase, UserPlus, Send, X as XIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import OnboardingBanner from '../../components/OnboardingBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardHome = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentCandidates, setRecentCandidates] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [queuedNotifs, setQueuedNotifs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchQueuedNotifs = async () => {
    try {
      const res = await axios.get(`${API}/notification-queue?status=pending`);
      setQueuedNotifs(res.data);
    } catch (e) {
      console.error('Error fetching queue:', e);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, candidatesRes, notificationsRes] = await Promise.all([
          axios.get(`${API}/analytics/dashboard`),
          axios.get(`${API}/candidates?status=pending_review`),
          axios.get(`${API}/notifications?unread_only=true`)
        ]);
        
        setStats(statsRes.data);
        setRecentCandidates(candidatesRes.data.slice(0, 5));
        setNotifications(notificationsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    fetchQueuedNotifs();
  }, []);

  const getScoreBadgeClass = (score) => {
    if (score >= 75) return 'bg-green-100 text-green-800 border-green-300';
    if (score >= 50) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded-2xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-home">
      {/* Onboarding Banner */}
      <OnboardingBanner />

      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#4B2D6E] font-[Lato]">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-[#666666] mt-1">Here's what's happening with your hiring pipeline</p>
        </div>
        <Link to="/dashboard/jobs">
          <Button className="bg-[#E8722A] hover:bg-[#D6621F] text-white" data-testid="post-job-btn">
            <Briefcase className="mr-2 h-4 w-4" />
            Post New Job
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-t-4 border-[#4B2D6E]" data-testid="stat-total-candidates">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#666666]">Total Candidates</p>
                <p className="text-3xl font-bold text-[#4B2D6E]">{stats?.total_candidates || 0}</p>
              </div>
              <div className="w-12 h-12 bg-[#4B2D6E]/10 rounded-full flex items-center justify-center">
                <Users className="h-6 w-6 text-[#4B2D6E]" />
              </div>
            </div>
            <p className="text-sm text-[#4CAF50] mt-2">
              +{stats?.this_week_applications || 0} this week
            </p>
          </CardContent>
        </Card>

        <Card className="border-t-4 border-[#E8722A]" data-testid="stat-pending-review">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#666666]">Pending Review</p>
                <p className="text-3xl font-bold text-[#E8722A]">{stats?.pending_review || 0}</p>
              </div>
              <div className="w-12 h-12 bg-[#E8722A]/10 rounded-full flex items-center justify-center">
                <Clock className="h-6 w-6 text-[#E8722A]" />
              </div>
            </div>
            <Link to="/dashboard/candidates?status=pending_review" className="text-sm text-[#4B2D6E] hover:underline mt-2 block">
              Review now →
            </Link>
          </CardContent>
        </Card>

        <Card className="border-t-4 border-[#1976D2]" data-testid="stat-interviews">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#666666]">Interviews Scheduled</p>
                <p className="text-3xl font-bold text-[#1976D2]">{stats?.interviews_scheduled || 0}</p>
              </div>
              <div className="w-12 h-12 bg-[#1976D2]/10 rounded-full flex items-center justify-center">
                <Calendar className="h-6 w-6 text-[#1976D2]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-t-4 border-[#4CAF50]" data-testid="stat-hired">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#666666]">Hired</p>
                <p className="text-3xl font-bold text-[#4CAF50]">{stats?.hired_count || 0}</p>
              </div>
              <div className="w-12 h-12 bg-[#4CAF50]/10 rounded-full flex items-center justify-center">
                <UserCheck className="h-6 w-6 text-[#4CAF50]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Candidates */}
        <Card className="border-t-4 border-[#4B2D6E]" data-testid="recent-candidates-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg text-[#4B2D6E] font-[Lato]">Candidates Awaiting Review</CardTitle>
            <Link to="/dashboard/candidates">
              <Button variant="ghost" size="sm" className="text-[#4B2D6E]">
                View All <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentCandidates.length === 0 ? (
              <div className="text-center py-8 text-[#666666]">
                <UserPlus className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No candidates awaiting review</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentCandidates.map((candidate) => (
                  <Link 
                    key={candidate.id} 
                    to={`/dashboard/candidates/${candidate.id}`}
                    className="flex items-center justify-between p-3 bg-[#F5F5F5] rounded-lg hover:bg-gray-200 transition-colors"
                    data-testid={`candidate-row-${candidate.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#333333] truncate">
                        {candidate.contact_info?.first_name 
                          ? `${candidate.contact_info.first_name} ${candidate.contact_info.last_name}`
                          : (candidate.parsed_resume?.name && candidate.parsed_resume.name !== 'N/A' && !candidate.parsed_resume.name.toLowerCase().includes('unable to extract')
                            ? candidate.parsed_resume.name 
                            : 'Unknown Candidate')}
                      </p>
                      <p className="text-sm text-[#666666]">{candidate.job_title}</p>
                    </div>
                    <Badge className={`ml-3 ${getScoreBadgeClass(candidate.ai_score)}`}>
                      {candidate.ai_score || '—'}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="border-t-4 border-[#E8722A]" data-testid="notifications-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg text-[#4B2D6E] font-[Lato]">
              Recent Notifications
              {stats?.unread_notifications > 0 && (
                <Badge className="ml-2 bg-[#E8722A] text-white">{stats.unread_notifications}</Badge>
              )}
            </CardTitle>
            <Link to="/dashboard/notifications">
              <Button variant="ghost" size="sm" className="text-[#4B2D6E]">
                View All <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-[#666666]">
                <Bell className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No new notifications</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notifications.map((notification) => (
                  <div 
                    key={notification.id} 
                    className="p-3 bg-[#F5F5F5] rounded-lg border-l-4 border-[#E8722A]"
                    data-testid={`notification-${notification.id}`}
                  >
                    <p className="text-sm text-[#333333]">{notification.message}</p>
                    <p className="text-xs text-[#666666] mt-1">
                      {new Date(notification.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Notification Queue */}
      {queuedNotifs.length > 0 && (
        <Card className="border-t-4 border-yellow-500" data-testid="notification-queue-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg text-[#4B2D6E] font-[Lato] flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-500" />
              Pending Notifications
              <Badge className="bg-yellow-100 text-yellow-800">{queuedNotifs.length} queued</Badge>
            </CardTitle>
            <Button
              size="sm"
              className="bg-[#E8722A] hover:bg-[#D6621F]"
              onClick={async () => {
                try {
                  const res = await axios.post(`${API}/notification-queue/send-all-now`);
                  toast.success(res.data.message);
                  fetchQueuedNotifs();
                } catch (e) {
                  toast.error('Failed to send notifications');
                }
              }}
              data-testid="send-all-now-btn"
            >
              <Send className="mr-1 h-4 w-4" /> Send All Now
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {queuedNotifs.map((n) => (
                <div key={n.id} className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex-1 min-w-0 mr-3">
                    <div className="flex items-center gap-2">
                      <Badge className={n.trigger_status === 'approved' ? 'bg-blue-100 text-blue-800' : n.trigger_status === 'hired' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {n.trigger_status}
                      </Badge>
                      <span className="text-sm font-medium text-[#333]">{n.candidate_name}</span>
                      <Badge variant="outline" className="text-xs">{n.channel}</Badge>
                    </div>
                    <p className="text-xs text-[#666] mt-1 truncate">{n.message}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-green-600 hover:bg-green-50 h-8 px-2"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/notification-queue/${n.id}/send-now`);
                          toast.success(`Sent to ${n.candidate_name}`);
                          fetchQueuedNotifs();
                        } catch (e) {
                          toast.error('Failed to send');
                        }
                      }}
                      data-testid={`send-now-${n.id}`}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:bg-red-50 h-8 px-2"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/notification-queue/${n.id}/cancel`);
                          toast.success('Notification cancelled');
                          fetchQueuedNotifs();
                        } catch (e) {
                          toast.error('Failed to cancel');
                        }
                      }}
                      data-testid={`cancel-notif-${n.id}`}
                    >
                      <XIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#999] mt-3 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              These will be sent automatically at your scheduled time. Use "Send All Now" to override.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-[#4B2D6E] to-[#6B4D8E] text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <TrendingUp className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-gray-200">Referral Applications</p>
                <p className="text-2xl font-bold">{stats?.referral_applications || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-[#E8722A] to-[#F89A5A] text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Users className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-gray-100">Active Employees</p>
                <p className="text-2xl font-bold">{stats?.active_employees || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-[#4CAF50] to-[#6FCF7C] text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <UserCheck className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-gray-100">This Week</p>
                <p className="text-2xl font-bold">+{stats?.this_week_applications || 0} applications</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardHome;
